import { useCallback, useMemo, useState } from 'react';
import type { EmployeeType } from '../../services/api/models/EmployeeType';
import type { EmployeeTypeAssignmentItem } from '../../services/api';
import type { OrgMemberResponse } from './types';

export type EmployeeTypeRow = {
  clinic_id: string;
  clinic_name: string;
  employee_type: EmployeeType | null;
  employee_type_other: string;
};

function buildSavedRows(member: OrgMemberResponse | null): EmployeeTypeRow[] {
  const assignments = member?.clinic_assignments ?? [];
  return assignments
    .map(a => ({
      clinic_id: a.clinic_id,
      clinic_name: a.clinic_name,
      employee_type: a.employee_type ?? null,
      employee_type_other: a.employee_type_other ?? '',
    }))
    .sort((a, b) => a.clinic_name.localeCompare(b.clinic_name));
}

function rowsEqual(a: EmployeeTypeRow[], b: EmployeeTypeRow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (row, i) =>
      row.clinic_id === b[i].clinic_id &&
      row.employee_type === b[i].employee_type &&
      row.employee_type_other === b[i].employee_type_other
  );
}

/** `member` must be referentially stable — ClinicianManager holds it in state. */
export function useEmployeeTypes(member: OrgMemberResponse | null) {
  const savedRows = useMemo(() => buildSavedRows(member), [member]);
  const [rows, setRows] = useState<EmployeeTypeRow[]>(savedRows);
  const [seededMember, setSeededMember] = useState(member);

  // Re-seed when a different member arrives. Both obvious alternatives are
  // wrong: `savedRows` identity is a useMemo result, which React may drop and
  // re-seed over edits in progress; comparing rows by value misses a member
  // switch when the two have matching saved rows, and the dialog never
  // unmounts (`open={!!member}`) so that state would carry across.
  if (seededMember !== member) {
    setSeededMember(member);
    setRows(savedRows);
  }

  const isDirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows]);

  const setEmployeeType = useCallback(
    (clinicId: string, employeeType: EmployeeType | null) => {
      setRows(prev =>
        prev.map(row =>
          row.clinic_id === clinicId
            ? {
                ...row,
                employee_type: employeeType,
                employee_type_other:
                  employeeType === 'OTHER' ? row.employee_type_other : '',
              }
            : row
        )
      );
    },
    []
  );

  const setEmployeeTypeOther = useCallback((clinicId: string, text: string) => {
    setRows(prev =>
      prev.map(row =>
        row.clinic_id === clinicId ? { ...row, employee_type_other: text } : row
      )
    );
  }, []);

  /** Rows with OTHER selected but no free text — blocks saving. */
  const invalidClinicIds = useMemo(
    () =>
      new Set(
        rows
          .filter(
            row =>
              row.employee_type === 'OTHER' && !row.employee_type_other.trim()
          )
          .map(row => row.clinic_id)
      ),
    [rows]
  );

  const getAssignments = useCallback(
    (): EmployeeTypeAssignmentItem[] =>
      rows.map(row => ({
        clinic_id: row.clinic_id,
        employee_type: row.employee_type,
        employee_type_other:
          row.employee_type === 'OTHER' ? row.employee_type_other.trim() : null,
      })),
    [rows]
  );

  return {
    rows,
    isDirty,
    invalidClinicIds,
    setEmployeeType,
    setEmployeeTypeOther,
    getAssignments,
  };
}
